import React, { useMemo, forwardRef } from 'react';

import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { PrimaryButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';

import { COMPONENTS } from 'components/Globals/Analytics/constants';
import ShareEntity from 'components/Globals/ShareEntity';
import TicketsButton, { getTicketsURL } from 'components/Productions/Display/TicketButton';

import { URL_STATIC_PATHS, CONTRIBUTORS, TP } from 'constants/index';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import usePageContext from 'utils/hooks/usePageContext';
import usePermissions from 'utils/permissions';
import { getEntityAndTypeFromContributor } from 'utils/productions';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './ProductionActions.module.scss';

const ProductionActions = forwardRef(({ production, trackingData, className, isSticky = false }, ref) => {
  const { t } = useTranslation('NS_CASTING_TOOL');
  const { navigate, entityType } = usePageContext();
  const { isAdmin } = usePermissions();
  const { isMobile } = useDeviceTypeLayouts();
  const ticketsExists = useMemo(() => getTicketsURL(production), [production]);
  const rentalsExists = production?.stats?.rentals?.exists;

  const showProductionButtons = ticketsExists || rentalsExists;
  const linkProps = navigate.getLinkProps({ entity: production, entityType: ENTITY_TYPES.PRODUCTION });

  const producer = production?.contributions?.find(
    prodContributor => prodContributor?.contributionType === CONTRIBUTORS.PRODUCER,
  );
  const { entity: producerEntity, entityType: producerEntityType } = getEntityAndTypeFromContributor(producer);

  return (
    <div className={classnames(classes.productionActions, className)} ref={ref}>
      <ShareEntity
        url={linkProps?.url}
        renderIconButton={isSticky}
        showLabelText
        entityType={ENTITY_TYPES.PRODUCTION}
        entity={production}
        className={classnames(showProductionButtons ? classes.shareIconButton : classes.button, {
          [classes.stickyShare]: isSticky,
        })}
        trackingData={{ ...trackingData, component: COMPONENTS.SHARE_CTA }}
      />
      {isAdmin && producer?.id && (
        <PrimaryButton
          leftIcon={<SpriteIcon icon="edit" />}
          {...navigate.getLinkProps({
            entityType: producerEntityType,
            entity: producerEntity,
            edit: true,
            onlyLinkProps: true,
            path: URL_STATIC_PATHS.MODIFY_PRODUCTION,
            queryParams: { production_id: production?.id },
          })}
          isLink
          size="medium"
          styles={{
            root: classes.editAction,
          }}
          trackingData={{
            ...trackingData,
            component: COMPONENTS.EDIT_CTA,
          }}
        >
          {t(`${TP}.m_EDIT`)}
        </PrimaryButton>
      )}
      {showProductionButtons && (
        <div className={classes.productionButtons}>
          <TicketsButton
            production={production}
            styles={{ root: classnames(classes.button, classes.ticketsButton), icon: classes.ticketsIcon }}
            size="medium"
            trackingData={trackingData}
            showTicketPrice={entityType === ENTITY_TYPES.PRODUCTION && !isMobile}
            hasLeftIcon={entityType === ENTITY_TYPES.PRODUCTION && !isMobile}
          />
        </div>
      )}
    </div>
  );
});

export default ProductionActions;
